import React, { useEffect, useRef } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaTrash, FaPlus, FaGripVertical } from 'react-icons/fa';

// SortableInput with drag effect
function SortableInput({ input, index, onChange, onRemove, attributes, listeners, isDragging, setNodeRef, style }) {
    return (
        <div
            ref={setNodeRef}
            style={{
                ...style,
                opacity: isDragging ? 0.85 : 1,
                background: isDragging ? '#e0e7ff' : '#f9fafb',
                transition: 'background 0.2s, box-shadow 0.2s, transform 0.18s cubic-bezier(.4,2,.6,1)',
                boxShadow: isDragging
                    ? '0 8px 32px 0 rgba(59,130,246,0.18)'
                    : '0 1px 4px 0 rgba(0,0,0,0.03)',
                zIndex: isDragging ? 100 : undefined,
                position: 'relative',
            }}
            className="flex flex-col gap-2 border border-blue-100 rounded-xl p-4 relative group hover:shadow-lg transition-shadow"
        >
            {/* Drag handle */}
            <button
                type="button"
                {...attributes}
                {...listeners}
                className="absolute left-2 top-4 cursor-grab text-blue-400 hover:text-blue-600 bg-white rounded-full p-1 shadow group-hover:scale-110 transition-transform"
                style={{ fontSize: 22, zIndex: 2 }}
                title="Arraste para reordenar"
                tabIndex={-1}
            >
                <FaGripVertical />
            </button>
            {/* First row: type and label */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 pl-7">
                <div className="flex flex-col gap-1 w-28">
                    <label className="text-xs text-gray-500 font-medium pl-1">Tipo</label>
                    <select
                        className="input input-bordered input-sm w-full bg-white"
                        value={input.type}
                        onChange={e => onChange(index, { ...input, type: e.target.value })}
                    >
                        <option value="text">Texto</option>
                        <option value="email">Email</option>
                        <option value="phone">Telefone</option>
                        <option value="number">Número</option>
                        

                    </select>
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-[120px]">
                    <label className="text-xs text-gray-500 font-medium pl-1">Label</label>
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full bg-white"
                        placeholder="Ex: Nome completo"
                        value={input.label}
                        onChange={e => onChange(index, { ...input, label: e.target.value })}
                    />
                </div>
            </div>
            {/* Second row: placeholder */}
            <div className="flex flex-col gap-1 pl-7">
                <label className="text-xs text-gray-500 font-medium pl-1">Placeholder</label>
                <input
                    type="text"
                    className="input input-bordered input-sm w-full bg-white"
                    placeholder="Ex: Qual o seu nome?"
                    value={input.placeholder}
                    onChange={e => onChange(index, { ...input, placeholder: e.target.value })}
                />
            </div>

            <div className="flex flex-col gap-1 pl-7">
                <label className="text-xs text-gray-500 font-medium pl-1">Indentificação</label>
                <input
                    type="text"
                    className="input input-bordered input-sm w-full bg-white"
                    placeholder="Ex: nome_completo"
                    value={input.name}
                    onChange={e => onChange(index, { ...input, name: e.target.value })}
                />
            </div>

            <div className="flex flex-col gap-1 pl-7 justify-end items-end">
                <div className="flex flex-row items-center gap-2">
                    <input
                    type="checkbox"
                    className="input input-bordered input-sm bg-white"
                    checked={input.required}
                    onChange={e => onChange(index, { ...input, required: e.target.checked })}
                />                <label className="text-xs text-gray-500 font-medium pl-1">Obrigatório</label>

                </div>

            </div>
            {/* Remove button */}
            <button
                type="button"
                className="absolute right-2 top-4 opacity-0 group-hover:opacity-100 transition-opacity btn btn-circle btn-xs bg-red-50 hover:bg-red-100 text-red-500 border-none shadow"
                onClick={() => onRemove(index)}
                title="Remover campo"
                style={{ zIndex: 2 }}
            >
                <FaTrash />
            </button>
        </div>
    );
}

function SortableInputWrapper(props) {
    const { input, index, onChange, onRemove, activeId } = props;
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: input.id });
    
    // Add drag effect: scale up and shadow when dragging or active
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging || activeId === input.id
            ? {
                boxShadow: '0 12px 32px 0 rgba(59,130,246,0.22)',
                scale: '1.03',
            }
            : {}),
    };

    return (
        <SortableInput
            input={input}
            index={index}
            onChange={onChange}
            onRemove={onRemove}
            attributes={attributes}
            listeners={listeners}
            isDragging={isDragging}
            setNodeRef={setNodeRef}
            style={style}
        />
    );
}

const ConfigInput = ({ block, updateBlock }) => {
    // Ensure block.inputs exists and has unique ids
    const inputs = Array.isArray(block.inputs)
        ? block.inputs.map((input, idx) => ({ id: input.id || `input-${idx}`, ...input }))
        : [];
    console.log(inputs);
    // DnD Kit setup
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    // Track currently active id for effect
    const [activeId, setActiveId] = React.useState(null);

    // Handlers
    const handleInputChange = (idx, newInput) => {
        const newInputs = [...inputs];
        newInputs[idx] = { ...newInput, id: newInputs[idx].id };
        updateBlock(block.id, 'inputs', newInputs);
    };

    const handleRemove = (idx) => {
        const newInputs = inputs.filter((_, i) => i !== idx);
        updateBlock(block.id, 'inputs', newInputs);
    };

    const handleAdd = () => {
        const newInput = {
            id: `input-${Date.now()}`,
            type: 'text',
            label: '',
            placeholder: '',
        };
        updateBlock(block.id, 'inputs', [...inputs, newInput]);
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (active.id !== over?.id) {
            const oldIndex = inputs.findIndex(i => i.id === active.id);
            const newIndex = inputs.findIndex(i => i.id === over.id);
            const newInputs = arrayMove(inputs, oldIndex, newIndex);
            updateBlock(block.id, 'inputs', newInputs);
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    // Optional: scroll into view when dragging
    const draggingRef = useRef();
    useEffect(() => {
        if (activeId && draggingRef.current) {
            draggingRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [activeId]);

    function handleBorderChange(e) {
        if(e.target.checked){
            updateBlock(block.id, 'border', true);
        }else{
            updateBlock(block.id, 'border', false);
        }
    }

    return (
        <div className="flex flex-col gap-3 w-full">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext
                    items={inputs.map(i => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {inputs.map((input, idx) => (
                        <div
                            key={input.id}
                            ref={activeId === input.id ? draggingRef : undefined}
                        >
                            <SortableInputWrapper
                                input={input}
                                index={idx}
                                onChange={handleInputChange}
                                onRemove={handleRemove}
                                activeId={activeId}
                            />
                        </div>
                    ))}
                </SortableContext>
            </DndContext>
            <button
                type="button"
                className="btn btn-outline btn-sm mt-2 flex items-center gap-2"
                onClick={handleAdd}
            >
                <FaPlus /> Adicionar campo
            </button>

            <div className="flex flex-col gap-2">
                <div className="block font-semibold mb-2 flex items-center gap-2">Bordas  
                    <input type="checkbox" className="w-4 h-4 rounded-full border-2 border-white shadow"  onChange={handleBorderChange} />
                </div>
                
             </div> 
        </div>
    );
};

export default ConfigInput;